import i18n from "../../../i18n";import { Trans } from "react-i18next";import { getInitials } from "../utils/orgTree";
import { ChevronUpIcon, ChevronDownIcon, WarningIcon } from "./Icons";

// =============================================================================
// PersonCard.jsx — the visual building block of the org chart.
// Exports three things:
//   Avatar        — photo-or-initials circle, reused everywhere a person shows up
//   PersonCard    — the desktop box card (with connector chevrons)
//   PersonListRow — the mobile indented-list row (same info, different layout)
// =============================================================================

// Avatar with initials fallback — used on cards, rows, search results, and
// the popover. Per the PRD: "A card photo is missing → initials avatar."
// small: shrinks the circle (used in list rows and search results).
export function Avatar({ person, small = false, size }) {
  const dims = {
    sm: "w-[38px] h-[38px] text-[13px] mb-0",
    md: "w-[52px] h-[52px] text-[17px] mb-2.5",
    lg: "w-[56px] h-[56px] text-[19px] mb-0"
  };
  const cls = dims[size ?? (small ? "sm" : "md")];
  return (
    <div
      className={`${cls} rounded-full bg-[#E3E9FA] text-primary flex items-center justify-center font-bold shrink-0 overflow-hidden`}>
      
      {person.photoUrl ?
      <img src={person.photoUrl} alt={person.name} className="w-full h-full object-cover" /> :
      getInitials(person.name)}
    </div>);

}

// ---------------------------------------------------------------------
// PersonCard — desktop org-chart node card.
// This is rendered once per visible person in OrgChartPage.jsx's TreeNode.
//
// States handled here (per PRD): default, focused (current centre),
// hover (Tailwind hover: utilities below), warning (part of a circular loop).
//
// Props:
//   person         — the employee record { empId, name, designation, department, photoUrl, ... }
//   isFocus        — true only for the card currently centred in the chart
//   isMe           — true only for the logged-in user's own card (shows "YOU" tag)
//   warning        — true if this card is part of a broken/circular reporting line
//   reporteeCount  — number of direct reportees; the expand-down pill is
//                     hidden entirely when this is 0 (PRD: "no expand-down
//                     control on a card with no reportees")
//   expanded       — whether this card's reportees are currently shown
//                     (controls the chevron direction + pill fill color)
//   onToggleDown   — click handler for the expand-down pill
//   showUp         — whether to render the expand-up chevron on the top
//                     edge (only true for the topmost visible card, and only
//                     when there's more manager chain above — see OrgChartPage.jsx)
//   onExpandUp     — click handler for the expand-up chevron
//   onOpen         — clicking anywhere else on the card opens the Person
//                     Detail Popover with this person
// -----------------------------------------------------------------------
export default function PersonCard({
  person,
  isFocus = false,
  isMe = false,
  warning = false,
  reporteeCount = 0,
  expanded = false,
  onToggleDown,
  showUp = false,
  onExpandUp,
  onOpen
}) {
  const cls =
  "relative w-[200px] mb-3.5 bg-white border rounded-xl px-4 pt-[18px] pb-5 shadow-[0_1px_3px_rgba(0,0,0,0.05)] " +
  "flex flex-col items-center text-center cursor-pointer transition-[box-shadow,transform,border-color] duration-150 " +
  "hover:shadow-[0_4px_14px_rgba(30,51,131,0.12)] hover:-translate-y-px active:translate-y-0 " + (
  isFocus ?
  "border-2 border-accent shadow-[0_4px_16px_rgba(61,90,241,0.18)] " :
  warning ?
  "border-[#F59E0B] " :
  "border-slate-200 ");

  return (
    <div className={cls} onClick={() => onOpen?.(person)}>
      {/* Expand-up chevron floats on the top edge — stopPropagation so
           clicking it doesn't also trigger onOpen (opening the popover) */}
      {showUp &&
      <button
        className="absolute -top-3.5 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-white border border-border text-primary flex items-center justify-center cursor-pointer shadow-[0_1px_4px_rgba(0,0,0,0.08)] transition-colors hover:bg-[#EEF2FF] hover:border-accent z-[2]"
        aria-label={i18n.t("orgCharts.showManager")}
        onClick={(e) => {e.stopPropagation();onExpandUp?.();}}>
        
          <ChevronUpIcon />
        </button>
      }

      {isMe &&
      <span className="absolute top-2.5 left-2.5 bg-accent text-white text-[10px] font-bold tracking-[0.05em] rounded-full px-2 py-0.5">
          <Trans i18nKey="orgCharts.you" />
        </span>
      }

      {warning &&
      <span
        className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-[#FEF3C7] text-[#B45309] flex items-center justify-center cursor-help"
        title={i18n.t("validation.circularReportingLineFixTheManagerInOnboarding")}>
        
          <WarningIcon />
        </span>
      }

      <Avatar person={person} />
      <p className="m-0 text-sm font-semibold text-foreground leading-[1.3]">{person.name}</p>
      <p className="mt-[3px] mb-0 text-xs font-medium text-accent">{person.designation}</p>
      <p className="mt-[3px] mb-0 text-[11px] text-muted-foreground">{person.department}</p>

      {/* Expand-down pill only renders when there's someone to expand into,
           and only if the parent passed a handler for it (e.g. the repeated
           "loop" card in TreeNode doesn't pass onToggleDown, so it never
           shows this control even if reporteeCount were nonzero). */}
      {reporteeCount > 0 && onToggleDown &&
      <button
        className={
        "absolute -bottom-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1 h-[26px] px-2.5 rounded-full " +
        "text-xs font-semibold shadow-[0_1px_4px_rgba(0,0,0,0.08)] transition-colors z-[2] whitespace-nowrap cursor-pointer border " +
        "[&_svg]:transition-transform [&_svg]:duration-150 " + (
        expanded ?
        "bg-primary text-white border-primary hover:bg-[#162a68] [&_svg]:rotate-180" :
        "bg-white text-primary border-border hover:bg-[#EEF2FF] hover:border-accent")
        }
        aria-label={expanded ? "Collapse reportees" : "Show reportees"}
        onClick={(e) => {e.stopPropagation();onToggleDown();}}>
        
          {reporteeCount}
          <ChevronDownIcon size={12} />
        </button>
      }
    </div>);

}

// -----------------------------------------------------------------------
// PersonListRow — mobile equivalent of PersonCard, used below 768px.
// Same hierarchy as the desktop tree, but rendered as a flat vertical list
// with indentation (via the `depth` prop) instead of nested boxes — this is
// what keeps the mobile view from ever needing horizontal scrolling.
//
// Props mirror PersonCard where they overlap; `depth` (0, 1, 2, ...)
// controls the left-indent in px (18px per level, applied via the inline
// marginLeft style below).
// -----------------------------------------------------------------------
export function PersonListRow({
  person,
  depth = 0,
  isFocus = false,
  isMe = false,
  warning = false,
  reporteeCount = 0,
  expanded = false,
  onToggleDown,
  onOpen
}) {
  const cls =
  "flex items-center gap-2.5 bg-white border rounded-[10px] px-3 py-2.5 mb-2 " + (
  isFocus ? "border-2 border-accent" : warning ? "border-[#F59E0B]" : "border-slate-200");

  return (
    <div className={cls} style={{ marginLeft: depth * 18 }}>
      {/* Chevron toggle on the left, OR an empty spacer to keep names
           aligned when this row has no reportees to expand */}
      {reporteeCount > 0 && onToggleDown ?
      <button
        className={
        "w-[30px] h-[30px] rounded-lg border-none bg-slate-100 text-primary flex items-center justify-center cursor-pointer shrink-0 " +
        "[&_svg]:transition-transform [&_svg]:duration-150 " + (
        expanded ? "[&_svg]:rotate-180" : "")
        }
        aria-label={expanded ? "Collapse reportees" : "Show reportees"}
        onClick={() => onToggleDown()}>
        
          <ChevronDownIcon size={13} />
        </button> :

      <span className="w-[30px] shrink-0" />
      }

      <Avatar person={person} small />

      {/* Tapping the name/role area (not the chevron) opens the popover */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpen?.(person)}>
        <p className="m-0 text-sm font-semibold text-foreground flex items-center gap-1.5">
          {person.name}
          {isMe &&
          <span className="bg-accent text-white text-[9px] font-bold rounded-full px-1.5 py-px tracking-[0.05em]">
              <Trans i18nKey="orgCharts.you" />
            </span>
          }
          {warning &&
          <span className="text-[#B45309] flex items-center" title={i18n.t("orgCharts.circularReportingLine")}>
              <WarningIcon size={13} />
            </span>
          }
        </p>
        <p className="mt-0.5 mb-0 text-xs text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">
          {person.designation} · {person.department}
        </p>
      </div>

      {reporteeCount > 0 &&
      <span className="shrink-0 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#EEF2FF] text-primary text-[11px] font-bold flex items-center justify-center">
          {reporteeCount}
        </span>
      }
    </div>);

}
